import { DialogActions, Grid, Divider, Snackbar, Alert } from "@mui/material";
import Button from '../../../components/atoms/Button/Button'
import { Fragment, useState, FC, useRef } from "react";
import cloneDeep from "lodash/cloneDeep";
import FormData from "./FormData";
import { Cap, CapFormInput } from "../../../types/caps";
import { errorHandler } from "../../../utils/errors";
import { useDispatch } from "react-redux";
import {
  receiveUpdatedCap,
  receiveNewCap,
  receiveDeleteCap,
} from "../../../store/actions/Caps";
import Swal from "sweetalert2";
import { useForm, FormProvider } from "react-hook-form";
import moment from "moment";
import Loading from "../../atoms/Loading";
import { scrollToFirstError } from "../../../utils/scrollToError";
import { hasCapsChanges } from "../../utils/hasCapsChanges";
type CapsModalProps = {
  cap?: Cap;
  closeModal: () => void
};

const toInput = (c?: Cap) => ({
  name: c?.name ?? '',
  activeStart: c?.activeStart ?? moment(),
  activeEnd: c?.activeEnd ?? moment('12/31/2099', 'MM/DD/YYYY'),
  states: c?.states ?? [],
  origins: c?.origins.map(x => x.toString()) ?? [],
  campaigns: c?.campaigns.map(x => x.toString()) ?? [],
  customerTypes: c?.customerTypes ?? [],
  amount: c?.amount ?? null,
  loanType: c?.loanType ?? '',
  period: c?.period ?? ''
})

const toCap = (c: CapFormInput): Cap => ({
  capId: 0,
  name: c.name,
  amount: Number(c.amount),
  activeStart: c.activeStart ?? moment(),
  loanType: c.loanType,
  activeEnd: c.activeEnd ?? moment(),
  period: c.period,
  origins: c.origins.filter(x => Number(x) >= 0).map(x => Number(x)),
  campaigns: c.campaigns.filter(x => Number(x) >= 0).map(x => Number(x)),
  customerTypes: (c.customerTypes || []).map(Number),
  states: c.states
})
const CapsForm: FC<CapsModalProps> = ({ closeModal, cap }) => {
  const [defaultCap] = useState(toInput(cap));
  const [loading, setLoading] = useState(false);
  const [noChangesWarningOpen, setNoChangesWarningOpen] = useState(false);

 
  const originalCapRef = useRef<Cap | null>(cap ? cloneDeep(cap) : null);

  const methods = useForm<CapFormInput>(
    {
      defaultValues: defaultCap,
      shouldFocusError: false,
    }
  );
  const { handleSubmit } = methods;
  const formRef = useRef<HTMLFormElement>(null);

  const dispatch = useDispatch();
  const handleDeleteCap = async (capID: number) => {
    const deleteCap = async () => {
      try {
        setLoading(true)
        await dispatch(receiveDeleteCap(capID));
      } catch (err) {
        errorHandler(err);
      } finally {
        setLoading(false);
      }
      closeModal();
    }
    Swal.fire({
      title: "Are You Sure?",
      text: "Once you delete this cap, you will not be able to recover it.",
      icon: "warning",
      confirmButtonColor: "#016a01",
      showCancelButton: true,
      confirmButtonText: "Yes, I am sure.",
      cancelButtonText: "No, cancel this request.",
    }).then((result) => {
      if (result.value) {
        deleteCap()
      }
    });
  
  }
  const onSubmit = async (newCap: Cap) => {
    try {
      setLoading(true)
      if (newCap.capId === 0) {
        await dispatch(receiveNewCap(newCap));
      } else {
        await dispatch(receiveUpdatedCap(newCap));
      }
      closeModal();
    } catch (err) {
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  const upsert = async (c: CapFormInput) => {
    if (loading) return; 

    const newCap = toCap(c);
    newCap.capId = cap?.capId ?? 0;

  
    const originalCap = originalCapRef.current;
    if (originalCap && !hasCapsChanges(originalCap, newCap)) {
      setNoChangesWarningOpen(true);
      return;
    }

    await onSubmit(newCap);
  }

  return (
    <Fragment>  {loading && <Loading />}
      <FormProvider {...methods}>
        {/* Todo: handlesubmit */}
        <form
          ref={formRef}
          onSubmit={handleSubmit(
            upsert,
            (errors) => scrollToFirstError(formRef.current, errors)
          )}
        >
          <FormData editCap={cap ? { mode: true, id: cap.capId } : false} />
          <DialogActions>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              spacing={2}
            >
              <Grid item xs={12}>
                <Divider variant="fullWidth" />
              </Grid>
               {cap && (<Grid item xs={5}><Button
                    fullWidth
                    id={`caps-modal-delete-button`}
                    name="delete"
                    variant="contained"
                    color="error"
                    disabled={loading}
                    onClick={() => handleDeleteCap(cap.capId)}
                  >
                    Delete
                  </Button>  </Grid>)}

              <Grid item xs={5}>
                <Button
                  fullWidth
                  id={`caps-modal-${cap ? "edit" : "create"}-button`}
                  name="submit"
                  type={'submit'}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </FormProvider>
      <Snackbar
        open={noChangesWarningOpen}
        autoHideDuration={4000}
        onClose={() => setNoChangesWarningOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="warning" onClose={() => setNoChangesWarningOpen(false)} variant="filled">
          No changes to update
        </Alert>
      </Snackbar>
    </Fragment>
  );
};
export default CapsForm;
